import React, { useRef, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useAppSelector } from 'app-redux/hooks'

import DeckCardWrapper, {
  DeckCardWrapperChildProps,
  OverriddenActions,
} from 'components/card/deckCards/deckCardWrapper'
import DraggableBasicCard from 'components/card/DraggableBasicCard'
import CornerQuantity from 'components/card/controlsAndInfo/cornerQuantity'
import Prices from 'components/card/controlsAndInfo/prices'
import CornerOwned from 'components/card/controlsAndInfo/cornerOwned'
import CompanionTag from 'components/card/controlsAndInfo/companionTag'
import CanlanderPointsTag from 'components/card/controlsAndInfo/canlanderPointsTag'
import GameChangerLabel from 'components/card/controlsAndInfo/gameChangerLabel'
import Icon from 'components/elements/Icon'
import ContextMenu from 'components/elements/ContextMenu'
import CustomCardMarker from 'components/card/controlsAndInfo/customCardMarker'
import BasicCard from 'components/card/BasicCard'
import { AntiHighlight, MultiSelected } from 'components/card/controlsAndInfo/cardOverlays'
import { CardType, OWNED } from 'types/deck'

import { generateContextOptions } from 'components/card/deckCards/deckCardContextMenu'
import { disableExtraLayerKey } from 'components/accountSettingsPage/BrowserSpecificSiteSettings'

import styles from './imageCard.module.scss'

type Props = {
  cardId: string
  wrapperClassName?: string
  className?: string
  imageClassName?: string
  basicCardClass?: string
  stackName?: string
  skipOpenOnClick?: boolean
  hidePrices?: boolean
  simplifiedDOMStack?: boolean
  hideControlsUnlessHovered?: boolean
  hideMultiselectedHighlight?: boolean
  disableActions?: boolean
  disableFaded?: boolean
  standaloneCard?: CardType
  onCardUpdated?: (updatedCard: CardType) => Promise<void>
  actionsOverrides?: OverriddenActions
  noMultiSelect?: boolean
  disableDragging?: boolean
}

const ImageCard = ({
  cardId,
  stackName,
  className = '',
  basicCardClass = '',
  imageClassName = '',
  wrapperClassName = '',
  skipOpenOnClick = false,
  hidePrices = false,
  simplifiedDOMStack = false,
  hideControlsUnlessHovered = false,
  hideMultiselectedHighlight = false,
  disableActions = false,
  disableFaded = false,
  standaloneCard,
  onCardUpdated,
  actionsOverrides,
  noMultiSelect,
  disableDragging,
}: Props) => (
  <DeckCardWrapper
    standaloneCard={standaloneCard}
    onCardUpdated={onCardUpdated}
    cardId={cardId}
    stackName={stackName}
    component={Card}
    extraData={{
      className,
      basicCardClass,
      wrapperClassName,
      skipOpenOnClick,
      hidePrices,
      imageClassName,
      simplifiedDOMStack,
      hideControlsUnlessHovered,
      hideMultiselectedHighlight,
      disableActions,
      disableFaded,
      noMultiSelect,
      disableDragging,
    }}
    actionsOverrides={actionsOverrides}
  />
)

export default ImageCard

type ArchidektViewImageCardProps = { style?: React.CSSProperties } & Props

// Wraps the image card so it's possible to use transitions in the traditional "archidekt view" (stacks) (hover moves the card down view)
// Without this, the cards act as just 'normal' elements
export const ArchidektViewImageCard = (props: ArchidektViewImageCardProps) => (
  <div id={`deck-card-dom-${props.cardId}`} className={`${styles.container}`} style={props.style}>
    <ImageCard {...props} skipOpenOnClick />
  </div>
)

export const Card = ({
  card,
  stackName,
  localCardData,
  actions,
  extraData,
  deckData,
  userData,
}: DeckCardWrapperChildProps) => {
  const [isHovered, setIsHovered] = useState(false)

  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const imageCardLayers = useAppSelector(state => state.cacheables.imageCardLayers)

  const options = generateContextOptions({ card, actions, localCardData, deckData, extraData, userData }, false)

  const [{ [disableExtraLayerKey]: disableAnimatedLayers }] = useCookies([disableExtraLayerKey])

  const handleClick = (e: React.MouseEvent) => {
    if (extraData.disableActions) return
    if (!actions.cardClick) return
    if (e.detail > 1) return

    if (isHovered || !extraData.hideControlsUnlessHovered) actions.cardClick(e, extraData.skipOpenOnClick)
  }

  // More complex logic than I wanted to try to fit inline inthe jsx
  let showQtyAndColorTag = !!localCardData.quantity || !!card.qty || localCardData.saving === 'saving' // Default state
  if (!imageCardLayers.qtyAndColorTag) showQtyAndColorTag = false
  if (localCardData.quantity !== card.qty || localCardData.saving === 'saving') showQtyAndColorTag = true // override when neccesary

  // More complex logic than I wanted to try to fit inline inthe jsx
  let secondaryCategoryLayer = localCardData.inSecondaryCategory
  if (!imageCardLayers.secondaryCategoryIndicator) secondaryCategoryLayer = false
  if (extraData.disableFaded) secondaryCategoryLayer = false

  return (
    <div
      className={`
        ${styles.imageCard}
        ${extraData.hideControlsUnlessHovered ? styles.hideControlsWithoutHovered : ''}
        ${extraData.className}
      `}>
      {!!card.totalPrintingQtyInDeck && (
        <button
          className={card.qty ? styles.inDeckButton : styles.otherPrintingsButton}
          onClick={e =>
            card.relatedCardIds.length > 1 || card.relatedCardIds[0] !== card.id
              ? actions.onViewRelatedCards && actions.onViewRelatedCards()
              : actions.cardClick(e, extraData.skipOpenOnClick)
          }>
          Copies: {card.qty}
          {card.totalPrintingQtyInDeck !== card.qty && (
            <>
              {' '}
              (
              <span className={styles.othersHoverIndicator}>
                +{card.totalPrintingQtyInDeck - card.qty} other{card.totalPrintingQtyInDeck > 1 ? 's' : ''}
              </span>
              )
            </>
          )}
        </button>
      )}

      {extraData.disableDragging ? (
        <BasicCard
          className={extraData.basicCardClass}
          imageClassName={extraData.imageClassName}
          cardLoaderClassName={styles.cardLoader}
          card={card}
          flipped={localCardData.flipped}
          error={imageCardLayers.errorBorder && localCardData.cardError}
          overrideFoil={!imageCardLayers.foilLayer ? false : undefined}
          overrideImageUrl={!imageCardLayers.cardImage ? '' : undefined}
          faded={secondaryCategoryLayer}
          onClick={handleClick}
          onContextMenu={extraData.disableActions ? undefined : actions.rightClick}
          onMouseEnter={() => setTimeout(() => setIsHovered(true), 150)}
          onMouseLeave={() => setIsHovered(false)}
        />
      ) : (
        <DraggableBasicCard
          className={extraData.basicCardClass}
          imageClassName={extraData.imageClassName}
          cardLoaderClassName={styles.cardLoader}
          card={card}
          flipped={localCardData.flipped}
          error={imageCardLayers.errorBorder && localCardData.cardError}
          overrideFoil={!imageCardLayers.foilLayer ? false : undefined}
          overrideImageUrl={!imageCardLayers.cardImage ? '' : undefined}
          faded={secondaryCategoryLayer}
          onClick={handleClick}
          onContextMenu={extraData.disableActions ? undefined : actions.rightClick}
          dragItem={{ card, stackName, multiSelectedIds: localCardData.isMultiSelected ? multiSelectedIds : undefined }}
          disableDrag={extraData.disableActions}
          onDragStart={actions.onDragStart}
          onDragEnd={actions.onDragEnd}
          onMouseEnter={() => setTimeout(() => setIsHovered(true), 150)}
          onMouseLeave={() => setIsHovered(false)}
        />
      )}

      {!extraData.simplifiedDOMStack && (
        <>
          {/* Some phones don't play nice with the animated opacity and it'll get stuck */}
          {`${disableAnimatedLayers}` !== 'true' ? (
            <>
              <MultiSelected visible={localCardData.isMultiSelected && !extraData.hideMultiselectedHighlight} />
              <AntiHighlight visible={localCardData.antiHighlight} />
            </>
          ) : (
            <>
              {localCardData.isMultiSelected && (
                <MultiSelected
                  visible={localCardData.isMultiSelected && !extraData.hideMultiselectedHighlight}
                  simple
                />
              )}
              {localCardData.antiHighlight && <AntiHighlight visible={localCardData.antiHighlight} />}
            </>
          )}

          {showQtyAndColorTag && (
            <CornerQuantity
              card={{ ...card, qty: localCardData.quantity }}
              loader={localCardData.saving === 'saving'}
              onCardUpdate={actions.onCardUpdate}
            />
          )}

          {imageCardLayers.collectionRibbon && <CornerOwned owned={card.owned} />}

          {(extraData.hideMultiselectedHighlight || !localCardData.anyMultiSelected) && (
            <>
              {imageCardLayers.gameChangerIndicator && <GameChangerLabel card={card} />}
              {imageCardLayers.customCardIndicator && <CustomCardMarker card={card} />}
              <CompanionTag card={card} />
              {imageCardLayers.canlanderPoints && <CanlanderPointsTag card={card} />}

              <div className={styles.filpButtonContainer}>
                {localCardData.isFlipCard && imageCardLayers.flipButton && (
                  <button onClick={() => actions.setFlipped(!localCardData.flipped)}>
                    <Icon name="refresh" />
                  </button>
                )}

                {card.pinnedStatus !== OWNED.NO && imageCardLayers.pinnedCardStatus && (
                  <button
                    onClick={actions.addToArchidektPins}
                    className={`${card.pinnedStatus === OWNED.YES ? styles.exactInClipboard : styles.printingInClipboard}`}>
                    <Icon name="pin" />
                  </button>
                )}
              </div>

              {!extraData.simplifiedDOMStack && (
                <>
                  <div className={styles.controls}>
                    {localCardData.ownsDeck && imageCardLayers.qtyControls && (
                      <>
                        <button className={styles.increaseButton} onClick={actions.incrementCard}>
                          <Icon name="add" />
                        </button>
                        <button
                          className={styles.decreaseButton}
                          onClick={actions.decrementCard}
                          disabled={!localCardData.quantity}>
                          <Icon name="minus" />
                        </button>
                      </>
                    )}

                    {imageCardLayers.openDetailsButton && (
                      <ContextMenu
                        card={card}
                        localCardData={localCardData}
                        openMenuAsDrawerOnTouch
                        menuClassName={styles.extrasMenu}
                        options={options}
                        openOnLeftClick>
                        <button className={styles.cardDetailsButton}>
                          <Icon name="ellipsis horizontal" />
                        </button>
                      </ContextMenu>
                    )}
                  </div>

                  {!extraData.hidePrices && (
                    <div className={styles.extrasContainer} style={{ opacity: localCardData.antiHighlight ? 0.15 : 1 }}>
                      <div className={styles.extras}>
                        <Prices card={card} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
